/**
 * HierarchicalRiskReview — Step 6's view when `analysis_mode ===
 * 'hierarchical'`. Two outer tabs (Current / Child); Treatment
 * filter sits inside each tab via the FilterDrawer.
 */
import React, { useMemo, useState } from 'react';
import TableTabNav from '../../components/TableTabNav/TableTabNav';
import RiskTable from './RiskTable';
import ChildRisksTable, { type ChildRiskView } from './ChildRisksTable';
import type { RiskRecord } from './riskFieldSchema';
import type { HierarchicalModifications } from '../stepBodyProps';

const TAB_CURRENT = 'current';
const TAB_CHILD = 'child';

export interface HierarchicalRiskReviewProps {
  newRisks: RiskRecord[];
  childRisks: ChildRiskView[];
  editable: boolean;
  onAdd: () => void;
  onEdit: (risk: RiskRecord) => void;
  onDelete: (risk: RiskRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
  modifications: HierarchicalModifications;
  onToggleRelevance: (riskId: string, isRelevant: boolean) => void;
  onJustificationChange: (riskId: string, justification: string) => void;
  childInputsDisabled?: boolean;
  /** Persist child modifications on Done (Story 15.F). */
  onSaveChildModifications?: () => Promise<void> | void;
}

export default function HierarchicalRiskReview({
  newRisks,
  childRisks,
  editable,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  modifications,
  onToggleRelevance,
  onJustificationChange,
  childInputsDisabled,
  onSaveChildModifications,
}: HierarchicalRiskReviewProps) {
  const [activeTab, setActiveTab] = useState<string>(TAB_CURRENT);

  const childView: ChildRiskView[] = useMemo(
    () =>
      childRisks.map((c) => ({
        ...c,
        is_relevant: modifications[c.risk_id]?.is_relevant ?? c.is_relevant ?? true,
        relevance_justification:
          modifications[c.risk_id]?.relevance_justification ?? c.relevance_justification ?? '',
      })),
    [childRisks, modifications],
  );

  const handleToggle = (riskId: string) => {
    const current = childView.find((c) => c.risk_id === riskId);
    onToggleRelevance(riskId, !(current?.is_relevant !== false));
  };

  const tabs = [
    { key: TAB_CURRENT, label: `Current TARA (${newRisks.length})` },
    { key: TAB_CHILD, label: `Child TARAs (${childRisks.length})` },
  ];

  return (
    <>
      <TableTabNav
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Hierarchical risk review tabs"
        bleed={false}
        sticky={false}
      />

      {activeTab === TAB_CURRENT ? (
        <RiskTable
          risks={newRisks}
          editable={editable}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onExport={onExport}
          onImport={onImport}
          emptyMessage="No new risks yet for this TARA."
        />
      ) : (
        <ChildRisksTable
          risks={childView}
          onToggleRelevance={handleToggle}
          onJustificationChange={onJustificationChange}
          disabled={childInputsDisabled}
          onSave={onSaveChildModifications}
        />
      )}
    </>
  );
}
